/**
 * KeyAuditor — per-peer identity-key history with change detection.
 *
 * Mirrors the iOS `KeyAuditor` (`kaether_for_ios/Stoat/E2EE/KeyAuditor.swift`)
 * but uses IndexedDB for persistence instead of flat JSON files.
 *
 * Signal's KeyTransparency builds on a server-side verifiable log with
 * inclusion proofs. We don't have that server yet. Until we do, this module
 * records what we *locally* observe — every identity key seen for every user,
 * with timestamp and source — so:
 *
 *   1. The caller can emit a "key changed" banner or block outbound sends.
 *   2. When a real KT log lands, this same timeline is what we'll verify
 *      inclusion proofs against. Wire format and on-disk schema deliberately
 *      match what a future KT-proof field would attach to.
 *
 * Everything is local-only. No network calls.
 *
 * Clock note: `observedAt` is caller-supplied. The auditor records whatever
 * the caller passes and does NOT enforce monotonic ordering. If two
 * observations arrive out of order (e.g. a background sync that ran while
 * offline), both are appended in call order, preserving the full audit trail.
 * Callers that care about display order should sort by `observedAt` themselves.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/**
 * Where the identity key observation came from.
 *
 * - `preKeyBundle` — returned by KDS when initiating a session.
 * - `envelope`     — embedded in a received encrypted message header.
 * - `manual`       — user-initiated key verification flow.
 */
export type KeyObservationSource = "preKeyBundle" | "envelope" | "manual";

/**
 * A single identity-key observation for a peer.
 *
 * `identityPubKey` is stored as a `Uint8Array` in memory. The IndexedDB layer
 * persists it as a Base64 string to maximise compatibility (structured-clone
 * in some environments strips typed-array buffer transfer semantics).
 *
 * `rootKeyFingerprint` is optional — callers may supply a short human-readable
 * fingerprint (e.g. the first 16 hex chars of SHA-256) for display; the
 * auditor never derives it internally.
 */
export interface KeyObservationRecord {
  readonly userId: string;
  readonly identityPubKey: Uint8Array;
  readonly observedAt: number; // Unix ms
  readonly source: KeyObservationSource;
  readonly rootKeyFingerprint?: string;
}

/**
 * Verdict returned by `compareAgainstLatest`. Mirrors iOS `KeyAuditEventKind`.
 *
 * - `'first-observation'` — no prior record exists for this user.
 * - `'unchanged'`         — key bytes match the most-recent log entry.
 * - `'changed'`           — key bytes differ from the most-recent log entry.
 */
export type KeyChangeVerdict = "first-observation" | "unchanged" | "changed";

// ---------------------------------------------------------------------------
// Storage row (on-disk / IndexedDB representation)
// ---------------------------------------------------------------------------

/**
 * Internal row stored in IndexedDB. `identityPubKey` is Base64-encoded so
 * IndexedDB's structured-clone algorithm handles it uniformly across browsers.
 */
interface StoredObservationRow {
  /** Auto-incremented primary key — ensures append-only insertion order. */
  id?: number;
  userId: string;
  identityPubKeyB64: string; // Base64(identityPubKey)
  keyHashHex: string;        // lowercase hex SHA-256 of identityPubKey
  observedAt: number;
  source: KeyObservationSource;
  rootKeyFingerprint?: string;
}

// ---------------------------------------------------------------------------
// KeyAuditStore interface
// ---------------------------------------------------------------------------

/**
 * The contract callers depend on. `KeyAuditorIDB` is the default
 * implementation backed by IndexedDB.
 *
 * Append-only invariant: `record()` NEVER deletes existing entries. It only
 * appends. There is intentionally no `delete` or `clear` method exposed on
 * this interface; reset helpers exist on the concrete class for testing.
 */
export interface KeyAuditStore {
  /**
   * Append `observation` to the log and return the comparison verdict against
   * the entry that was most-recent before this call.
   *
   * The verdict is derived *before* the new record is appended. A subsequent
   * call with the same key bytes will therefore return `'unchanged'`.
   */
  record(observation: KeyObservationRecord): Promise<KeyChangeVerdict>;

  /**
   * Return the most-recently appended record for `userId`, or `null` if none
   * exists. "Most-recent" means the highest auto-increment `id` in the store,
   * which equals insertion order, not `observedAt` order (see clock note above).
   */
  latest(userId: string): Promise<KeyObservationRecord | null>;

  /**
   * Return up to `limit` records for `userId`, most-recently-inserted first.
   * Passing `limit = 0` returns all records.
   */
  history(userId: string, limit: number): Promise<KeyObservationRecord[]>;

  /**
   * Compare `observation.identityPubKey` against the most-recently inserted
   * record for the same `userId` WITHOUT mutating the store.
   *
   * Returns:
   *   - `'first-observation'` if no record exists yet.
   *   - `'unchanged'` if key bytes match.
   *   - `'changed'` if key bytes differ.
   */
  compareAgainstLatest(
    observation: Readonly<Pick<KeyObservationRecord, "userId" | "identityPubKey">>,
  ): Promise<KeyChangeVerdict>;
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

/** Encode a Uint8Array to Base64 (browser-safe). */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Decode a Base64 string to Uint8Array. */
function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/** Compute a lowercase hex SHA-256 fingerprint of raw bytes via SubtleCrypto. */
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Convert a `StoredObservationRow` back to a `KeyObservationRecord`. */
function rowToRecord(row: StoredObservationRow): KeyObservationRecord {
  const rec: KeyObservationRecord = {
    userId: row.userId,
    identityPubKey: base64ToUint8(row.identityPubKeyB64),
    observedAt: row.observedAt,
    source: row.source,
  };
  if (row.rootKeyFingerprint !== undefined) {
    return { ...rec, rootKeyFingerprint: row.rootKeyFingerprint };
  }
  return rec;
}

// ---------------------------------------------------------------------------
// IndexedDB schema
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "key_observations";

/**
 * Open (or upgrade) the IndexedDB database with the given name.
 *
 * Schema v1:
 *   Object store `key_observations`:
 *     keyPath:     `id` (auto-increment)
 *     index `by_userId`: on `userId` (non-unique, multi-entry disabled)
 *
 * The auto-increment `id` guarantees insertion order is recoverable even if
 * two records arrive with identical `observedAt` timestamps.
 */
function openDB(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
        // Index by userId so we can efficiently fetch per-user rows.
        store.createIndex("by_userId", "userId", { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error(`IDB blocked: ${dbName}`));
  });
}

// ---------------------------------------------------------------------------
// KeyAuditorIDB — default IndexedDB-backed implementation
// ---------------------------------------------------------------------------

/**
 * Default implementation of `KeyAuditStore` backed by IndexedDB.
 *
 * Each `KeyAuditorIDB` instance holds an open `IDBDatabase` handle. Call
 * `KeyAuditorIDB.open(dbName)` to construct one. Use a per-test UUID as
 * `dbName` to keep test runs fully isolated.
 *
 * `close()` releases the handle. `deleteDatabase(dbName)` removes the DB
 * entirely (used in tests for teardown).
 */
export class KeyAuditorIDB implements KeyAuditStore {
  private constructor(private readonly db: IDBDatabase) {}

  /** Open the database and return a ready `KeyAuditorIDB`. */
  static async open(dbName: string): Promise<KeyAuditorIDB> {
    const db = await openDB(dbName);
    return new KeyAuditorIDB(db);
  }

  /** Close the underlying IDB connection. */
  close(): void {
    this.db.close();
  }

  /**
   * Delete the database entirely. Useful for test teardown.
   * The instance should be `close()`d before calling this.
   */
  static deleteDatabase(dbName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.deleteDatabase(dbName);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      // onblocked fires when another connection is still open.
      req.onblocked = () => reject(new Error(`IDB delete blocked: ${dbName}`));
    });
  }

  // -------------------------------------------------------------------------
  // KeyAuditStore implementation
  // -------------------------------------------------------------------------

  async record(observation: KeyObservationRecord): Promise<KeyChangeVerdict> {
    const verdict = await this.compareAgainstLatest(observation);
    const keyHashHex = await sha256Hex(observation.identityPubKey);

    const row: StoredObservationRow = {
      userId: observation.userId,
      identityPubKeyB64: uint8ToBase64(observation.identityPubKey),
      keyHashHex,
      observedAt: observation.observedAt,
      source: observation.source,
      ...(observation.rootKeyFingerprint !== undefined
        ? { rootKeyFingerprint: observation.rootKeyFingerprint }
        : {}),
    };

    await this._put(row);
    return verdict;
  }

  async latest(userId: string): Promise<KeyObservationRecord | null> {
    const rows = await this._rowsForUser(userId);
    if (rows.length === 0) return null;
    return rowToRecord(rows[rows.length - 1]);
  }

  async history(userId: string, limit: number): Promise<KeyObservationRecord[]> {
    const rows = await this._rowsForUser(userId);
    // Most-recently inserted first.
    const reversed = [...rows].reverse();
    const sliced = limit > 0 ? reversed.slice(0, limit) : reversed;
    return sliced.map(rowToRecord);
  }

  async compareAgainstLatest(
    observation: Readonly<Pick<KeyObservationRecord, "userId" | "identityPubKey">>,
  ): Promise<KeyChangeVerdict> {
    const latestRecord = await this.latest(observation.userId);
    if (latestRecord === null) {
      return "first-observation";
    }

    // Compare via SHA-256 hash to avoid direct typed-array byte-by-byte loops
    // in the hot path (also mirrors the iOS `keyHashHex` comparison strategy).
    const newHash = await sha256Hex(observation.identityPubKey);
    const existingHash = await sha256Hex(latestRecord.identityPubKey);
    return newHash === existingHash ? "unchanged" : "changed";
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /** Insert a row into the object store (append-only). */
  private _put(row: StoredObservationRow): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.add(row);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Retrieve all rows for a given userId in insertion order (ascending by
   * auto-increment `id`).
   */
  private _rowsForUser(userId: string): Promise<StoredObservationRow[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const index = store.index("by_userId");
      const req = index.getAll(userId);
      req.onsuccess = () => resolve(req.result as StoredObservationRow[]);
      req.onerror = () => reject(req.error);
    });
  }
}
